import React, { useState, useEffect } from 'react'
import { useHistory } from 'react-router-dom'
import { orderBy } from 'lodash'
import {
  ArrowBack,
  AccessTime,
  Delete,
  Add,
  CalendarToday,
} from '@mui/icons-material'

import DeleteIcon from 'assets/icons/Delete.svg'
import CopiarIcon from 'assets/icons/Copiar.svg'

import { Header, Card, Footer, Loading } from 'components'
import * as Global from 'global/styles.global'

import * as S from './styles'

import {
  createWeeklySchedule,
  buildWeeklySchedulePayload,
  fetchWeeklySchedules,
  deleteWeeklySchedule,
  formatDateFromAPI,
  formatDayIntervals,
  SavedWeeklySchedule,
} from './services'

interface TimeSlot {
  id: string
  from: string
  to: string
}

interface DaySchedule {
  [key: string]: TimeSlot[]
}

interface Exception {
  id: string
  reason: string
  startDate: string
  startTime: string
  endTime: string
  error?: string
}

const DAYS_OF_WEEK = [
  { key: 'monday', label: 'Segunda-feira' },
  { key: 'tuesday', label: 'Terça-feira' },
  { key: 'wednesday', label: 'Quarta-feira' },
  { key: 'thursday', label: 'Quinta-feira' },
  { key: 'friday', label: 'Sexta-feira' },
  { key: 'saturday', label: 'Sábado' },
  { key: 'sunday', label: 'Domingo' },
]

const applyDateMask = (value: string): string => {
  const numbers = value.replace(/\D/g, '')
  let masked = ''

  if (numbers.length > 0) {
    masked = numbers.substring(0, 2)
  }
  if (numbers.length > 2) {
    masked += '/' + numbers.substring(2, 4)
  }
  if (numbers.length > 4) {
    masked += '/' + numbers.substring(4, 8)
  }

  return masked
}

const parseDate = (dateStr: string): Date | null => {
  if (dateStr.length !== 10) return null
  const [day, month, year] = dateStr.split('/')
  const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day))
  if (isNaN(date.getTime())) return null
  return date
}

const isValidDate = (dateStr: string): boolean => {
  if (dateStr.length !== 10) return false
  const date = parseDate(dateStr)
  if (!date) return false

  const [day, month, year] = dateStr.split('/').map(Number)
  return (
    date.getDate() === day &&
    date.getMonth() === month - 1 &&
    date.getFullYear() === year
  )
}

const addMonths = (date: Date, months: number): Date => {
  const result = new Date(date)
  result.setMonth(result.getMonth() + months)
  return result
}

interface Specialty {
  id: string
  name: string
  attendance_time: number
  slug?: string
  status?: string
}

const OpenSchedule: React.FC = () => {
  const history = useHistory()

  const [loading, setLoading] = useState(true)
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [dateError, setDateError] = useState('')
  const [daySchedules, setDaySchedules] = useState<DaySchedule>({
    monday: [{ id: '1', from: '00:00', to: '00:00' }],
    tuesday: [{ id: '1', from: '00:00', to: '00:00' }],
    wednesday: [{ id: '1', from: '00:00', to: '00:00' }],
    thursday: [{ id: '1', from: '00:00', to: '00:00' }],
    friday: [{ id: '1', from: '00:00', to: '00:00' }],
    saturday: [{ id: '1', from: '00:00', to: '00:00' }],
    sunday: [],
  })
  const [exceptions, setExceptions] = useState<Exception[]>([])

  const [userId, setUserId] = useState('')
  const [specialties, setSpecialties] = useState<Specialty[]>([])
  const [selectedSpecialty, setSelectedSpecialty] = useState<Specialty | null>(
    null
  )
  const [countries, setCountries] = useState<string[]>([])

  const [savedSchedules, setSavedSchedules] = useState<SavedWeeklySchedule[]>(
    []
  )

  const [showNewPeriodForm, setShowNewPeriodForm] = useState(false)

  const [deleteModal, setDeleteModal] = useState<{
    isOpen: boolean
    scheduleId: string
    startDate: string
    endDate: string
  }>({
    isOpen: false,
    scheduleId: '',
    startDate: '',
    endDate: '',
  })

  const loadSavedSchedules = (id: string, showFormIfEmpty = false) => {
    fetchWeeklySchedules(id, setSavedSchedules, setLoading, (schedules) => {
      if (showFormIfEmpty && schedules.length === 0) {
        setShowNewPeriodForm(true)
      }
    })
  }

  const getSpecialtyName = (specialtyId: string): string => {
    const specialty = specialties.find((s) => s.id === specialtyId)
    return specialty?.name || 'Especialidade'
  }

  const openDeleteModal = (schedule: SavedWeeklySchedule) => {
    setDeleteModal({
      isOpen: true,
      scheduleId: schedule._id,
      startDate: formatDateFromAPI(schedule.start_date),
      endDate: formatDateFromAPI(schedule.end_date),
    })
  }

  const closeDeleteModal = () => {
    setDeleteModal({
      isOpen: false,
      scheduleId: '',
      startDate: '',
      endDate: '',
    })
  }

  const confirmDeleteSchedule = async () => {
    const success = await deleteWeeklySchedule(
      deleteModal.scheduleId,
      setLoading
    )
    if (success) {
      loadSavedSchedules(userId)
    }
    closeDeleteModal()
  }

  const handleCopySchedule = (schedule: SavedWeeklySchedule) => {
    const newDaySchedules: DaySchedule = {
      monday: [],
      tuesday: [],
      wednesday: [],
      thursday: [],
      friday: [],
      saturday: [],
      sunday: [],
    }

    const dayKeyMap: { [key: number]: string } = {
      1: 'monday',
      2: 'tuesday',
      3: 'wednesday',
      4: 'thursday',
      5: 'friday',
      6: 'saturday',
      7: 'sunday',
    }

    schedule.weekly_intervals.forEach((wi) => {
      const dayKey = dayKeyMap[wi.day_of_week]
      if (dayKey) {
        newDaySchedules[dayKey] = wi.intervals.map((interval, index) => ({
          id: `${Date.now()}-${index}`,
          from: interval.start,
          to: interval.end,
        }))
      }
    })

    setDaySchedules(newDaySchedules)

    const specialty = specialties.find((s) => s.id === schedule.specialty_id)
    if (specialty) {
      setSelectedSpecialty(specialty)
    }

    setExceptions([])

    setShowNewPeriodForm(true)

    setTimeout(() => {
      window.scrollTo({ top: 600, behavior: 'smooth' })
    }, 100)
  }

  useEffect(() => {
    const userDataStorage = window.localStorage.getItem('@StarBem:user')
    if (userDataStorage) {
      const userData = JSON.parse(userDataStorage)
      setUserId(userData.id)

      const activeSpecialties = orderBy(
        userData.specialties?.filter((item: any) => item.status === 'active') ||
          [],
        ['name'],
        ['asc']
      )
      setSpecialties(activeSpecialties)

      if (activeSpecialties.length > 0) {
        setSelectedSpecialty(activeSpecialties[0])
      }

      setCountries(userData.countries || [])

      if (userData.id) {
        loadSavedSchedules(userData.id, true)
      }
    }
  }, [])

  const goBack = () => {
    history.push('/configurar-agenda')
  }

  const handleStartDateChange = (value: string) => {
    const masked = applyDateMask(value)
    setStartDate(masked)
    setDateError('')

    if (masked.length === 10 && endDate.length === 10) {
      validateDates(masked, endDate)
    }
  }

  const handleEndDateChange = (value: string) => {
    const masked = applyDateMask(value)
    setEndDate(masked)
    setDateError('')

    if (masked.length === 10 && startDate.length === 10) {
      validateDates(startDate, masked)
    }
  }

  const validateDates = (start: string, end: string): boolean => {
    if (!isValidDate(start)) {
      setDateError('Data de início inválida')
      return false
    }

    if (!isValidDate(end)) {
      setDateError('Data de fim inválida')
      return false
    }

    const startDateParsed = parseDate(start)
    const endDateParsed = parseDate(end)

    if (!startDateParsed || !endDateParsed) {
      setDateError('Datas inválidas')
      return false
    }

    if (endDateParsed < startDateParsed) {
      setDateError('A data de fim não pode ser anterior à data de início')
      return false
    }

    const maxEndDate = addMonths(startDateParsed, 3)
    if (endDateParsed > maxEndDate) {
      setDateError(
        'A data de fim não pode ser mais de 3 meses após a data de início'
      )
      return false
    }

    setDateError('')
    return true
  }

  const addTimeSlot = (day: string) => {
    const newId = Date.now().toString()
    setDaySchedules((prev) => ({
      ...prev,
      [day]: [...prev[day], { id: newId, from: '00:00', to: '00:00' }],
    }))
  }

  const removeTimeSlot = (day: string, slotId: string) => {
    setDaySchedules((prev) => ({
      ...prev,
      [day]: prev[day].filter((slot) => slot.id !== slotId),
    }))
  }

  const updateTimeSlot = (
    day: string,
    slotId: string,
    field: 'from' | 'to',
    value: string
  ) => {
    setDaySchedules((prev) => ({
      ...prev,
      [day]: prev[day].map((slot) =>
        slot.id === slotId ? { ...slot, [field]: value } : slot
      ),
    }))
  }

  const addException = () => {
    const newId = Date.now().toString()
    setExceptions((prev) => [
      ...prev,
      {
        id: newId,
        reason: '',
        startDate: '',
        startTime: '00:00',
        endTime: '00:00',
      },
    ])
  }

  const removeException = (exceptionId: string) => {
    setExceptions((prev) => prev.filter((ex) => ex.id !== exceptionId))
  }

  const updateException = (
    exceptionId: string,
    field: 'reason' | 'startDate' | 'startTime' | 'endTime',
    value: string
  ) => {
    setExceptions((prev) =>
      prev.map((ex) => {
        if (ex.id !== exceptionId) return ex

        if (field === 'startDate') {
          const masked = applyDateMask(value)
          return { ...ex, startDate: masked }
        }

        return { ...ex, [field]: value }
      })
    )
  }

  const handleSubmit = async () => {
    if (!startDate || !endDate) {
      setDateError('Por favor, preencha as datas de início e fim')
      return
    }

    if (!validateDates(startDate, endDate)) {
      return
    }

    if (!selectedSpecialty) {
      setDateError('Por favor, selecione uma especialidade')
      return
    }

    const payload = buildWeeklySchedulePayload(
      startDate,
      endDate,
      daySchedules,
      exceptions,
      {
        id: selectedSpecialty.id,
        name: selectedSpecialty.name,
        attendance_time: selectedSpecialty.attendance_time || 30,
      },
      userId,
      countries,
      ['Plantão']
    )

    const success = await createWeeklySchedule(payload, setLoading)

    if (success) {
      setShowNewPeriodForm(false)

      loadSavedSchedules(userId)

      setStartDate('')
      setEndDate('')
      setDaySchedules({
        monday: [{ id: '1', from: '00:00', to: '00:00' }],
        tuesday: [{ id: '1', from: '00:00', to: '00:00' }],
        wednesday: [{ id: '1', from: '00:00', to: '00:00' }],
        thursday: [{ id: '1', from: '00:00', to: '00:00' }],
        friday: [{ id: '1', from: '00:00', to: '00:00' }],
        saturday: [{ id: '1', from: '00:00', to: '00:00' }],
        sunday: [],
      })
      setExceptions([])
    }
  }

  const applyRecommendedTemplate = () => {
    setShowNewPeriodForm(true)

    setDaySchedules({
      monday: [
        { id: '1', from: '08:00', to: '12:00' },
        { id: '2', from: '14:00', to: '18:00' },
      ],
      tuesday: [
        { id: '1', from: '08:00', to: '12:00' },
        { id: '2', from: '14:00', to: '18:00' },
      ],
      wednesday: [
        { id: '1', from: '08:00', to: '12:00' },
        { id: '2', from: '14:00', to: '18:00' },
      ],
      thursday: [
        { id: '1', from: '08:00', to: '12:00' },
        { id: '2', from: '14:00', to: '18:00' },
      ],
      friday: [
        { id: '1', from: '08:00', to: '12:00' },
        { id: '2', from: '14:00', to: '18:00' },
      ],
      saturday: [{ id: '1', from: '08:00', to: '12:00' }],
      sunday: [],
    })

    setTimeout(() => {
      window.scrollTo({ top: 600, behavior: 'smooth' })
    }, 100)
  }

  return (
    <Global.Container>
      {loading && <Loading title="Carregando..." />}
      <Header />
      <S.PageContainer>
        <Card>
          <S.PageHeader>
            <button type="button" className="back-button" onClick={goBack}>
              <ArrowBack style={{ fontSize: 20 }} />
            </button>
            <h2>Abertura de horários</h2>
          </S.PageHeader>

          <S.PageContent>
            {savedSchedules.length > 0 && (
              <S.SavedSchedulesSection>
                <div className="saved-section-header">
                  <p className="saved-section-description">
                    Cadastre, visualize e edite os períodos em que você estará
                    disponível para agendamentos de consultas
                  </p>
                  <div className="header-buttons">
                    <button
                      type="button"
                      className="new-period-button"
                      onClick={() => {
                        setShowNewPeriodForm(true)
                        setTimeout(() => {
                          window.scrollTo({ top: 600, behavior: 'smooth' })
                        }, 100)
                      }}
                    >
                      Cadastrar novo período
                    </button>
                    <button
                      type="button"
                      className="template-button-header"
                      onClick={applyRecommendedTemplate}
                    >
                      Cadastrar modelo recomendado ✨
                    </button>
                  </div>
                </div>

                <h4 className="saved-title">Período cadastrado:</h4>

                {savedSchedules.map((schedule) => (
                  <div className="saved-schedule-card" key={schedule._id}>
                    <div className="card-header">
                      <p className="period-dates">
                        Período ({formatDateFromAPI(schedule.start_date)} até{' '}
                        {formatDateFromAPI(schedule.end_date)}) -{' '}
                        {getSpecialtyName(schedule.specialty_id)}
                      </p>
                      <div className="card-actions">
                        <button
                          type="button"
                          className="action-button delete"
                          onClick={() => openDeleteModal(schedule)}
                          title="Excluir período"
                        >
                          <img src={DeleteIcon} alt="Excluir" />
                        </button>
                        <button
                          type="button"
                          className="action-button copy"
                          onClick={() => handleCopySchedule(schedule)}
                          title="Copiar para novo período"
                        >
                          <img src={CopiarIcon} alt="Copiar" />
                        </button>
                      </div>
                    </div>

                    <div className="week-grid">
                      <div className="day-column">
                        <span className="day-name">Segunda-feira</span>
                        <span className="day-hours">
                          {formatDayIntervals(schedule.weekly_intervals, 1)}
                        </span>
                      </div>
                      <div className="day-column">
                        <span className="day-name">Terça-feira</span>
                        <span className="day-hours">
                          {formatDayIntervals(schedule.weekly_intervals, 2)}
                        </span>
                      </div>
                      <div className="day-column">
                        <span className="day-name">Quarta-feira</span>
                        <span className="day-hours">
                          {formatDayIntervals(schedule.weekly_intervals, 3)}
                        </span>
                      </div>
                      <div className="day-column">
                        <span className="day-name">Quinta-feira</span>
                        <span className="day-hours">
                          {formatDayIntervals(schedule.weekly_intervals, 4)}
                        </span>
                      </div>
                      <div className="day-column">
                        <span className="day-name">Sexta-feira</span>
                        <span className="day-hours">
                          {formatDayIntervals(schedule.weekly_intervals, 5)}
                        </span>
                      </div>
                      <div className="day-column">
                        <span className="day-name">Sábado</span>
                        <span className="day-hours">
                          {formatDayIntervals(schedule.weekly_intervals, 6)}
                        </span>
                      </div>
                      <div className="day-column">
                        <span className="day-name">Domingo</span>
                        <span className="day-hours">
                          {formatDayIntervals(schedule.weekly_intervals, 7)}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </S.SavedSchedulesSection>
            )}

            {showNewPeriodForm && (
              <>
                <S.DateRangeSection>
                  <div className="date-range-header">
                    <div>
                      <p className="section-title">
                        Selecione a data de início e o fim:
                      </p>
                      <p className="section-subtitle">
                        Poderá definir sua disponibilidade até no máximo 3 meses
                      </p>
                    </div>
                    <button
                      type="button"
                      className="template-button"
                      onClick={applyRecommendedTemplate}
                    >
                      Usar modelo recomendado ✨
                    </button>
                  </div>

                  <div className="date-inputs">
                    {specialties.length > 1 && (
                      <div className="input-group">
                        <label>Área de atuação</label>
                        <select
                          value={selectedSpecialty?.id || ''}
                          onChange={(e) => {
                            const specialty = specialties.find(
                              (s) => s.id === e.target.value
                            )
                            if (specialty) setSelectedSpecialty(specialty)
                          }}
                        >
                          {specialties.map((specialty) => (
                            <option key={specialty.id} value={specialty.id}>
                              {specialty.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                    <div className="input-group">
                      <label>Data de início</label>
                      <div className="input-wrapper">
                        <CalendarToday style={{ fontSize: 16 }} />
                        <input
                          type="text"
                          placeholder="DD/MM/AAAA"
                          value={startDate}
                          onChange={(e) =>
                            handleStartDateChange(e.target.value)
                          }
                          maxLength={10}
                        />
                      </div>
                    </div>
                    <div className="input-group">
                      <label>Data de fim</label>
                      <div className="input-wrapper">
                        <CalendarToday style={{ fontSize: 16 }} />
                        <input
                          type="text"
                          placeholder="DD/MM/AAAA"
                          value={endDate}
                          onChange={(e) => handleEndDateChange(e.target.value)}
                          maxLength={10}
                        />
                      </div>
                    </div>
                  </div>
                  {dateError && <p className="date-error">{dateError}</p>}
                </S.DateRangeSection>

                <S.WeekScheduleGrid>
                  {DAYS_OF_WEEK.map((day) => (
                    <S.DayCard
                      key={day.key}
                      isEmpty={daySchedules[day.key].length === 0}
                    >
                      <div className="day-header">
                        <span className="day-name">{day.label}</span>
                        <button
                          type="button"
                          className="add-time-button"
                          onClick={() => addTimeSlot(day.key)}
                        >
                          Adicionar horário <Add style={{ fontSize: 14 }} />
                        </button>
                      </div>

                      {daySchedules[day.key].length === 0 ? (
                        <p className="no-schedule-text">
                          Você não possui horários configurados
                        </p>
                      ) : (
                        daySchedules[day.key].map((slot) => (
                          <div className="time-slot" key={slot.id}>
                            <div className="time-input-group">
                              <label>De</label>
                              <div className="time-input-wrapper">
                                <AccessTime style={{ fontSize: 14 }} />
                                <input
                                  type="time"
                                  value={slot.from}
                                  onChange={(e) =>
                                    updateTimeSlot(
                                      day.key,
                                      slot.id,
                                      'from',
                                      e.target.value
                                    )
                                  }
                                />
                              </div>
                            </div>
                            <div className="time-input-group">
                              <label>Até</label>
                              <div className="time-input-wrapper">
                                <AccessTime style={{ fontSize: 14 }} />
                                <input
                                  type="time"
                                  value={slot.to}
                                  onChange={(e) =>
                                    updateTimeSlot(
                                      day.key,
                                      slot.id,
                                      'to',
                                      e.target.value
                                    )
                                  }
                                />
                              </div>
                            </div>
                            <button
                              type="button"
                              className="remove-slot-button"
                              onClick={() => removeTimeSlot(day.key, slot.id)}
                            >
                              <Delete style={{ fontSize: 16 }} />
                            </button>
                          </div>
                        ))
                      )}
                    </S.DayCard>
                  ))}
                </S.WeekScheduleGrid>

                <S.ExceptionsSection>
                  <div className="exceptions-header">
                    <div>
                      <h3>Exceções:</h3>
                      <p>
                        Selecione o período de exceções para bloquear
                        automaticamente a sua agenda
                      </p>
                    </div>
                    <button
                      type="button"
                      className="add-exception-button"
                      onClick={addException}
                    >
                      Adicionar ausência <Add style={{ fontSize: 14 }} />
                    </button>
                  </div>

                  {exceptions.length === 0 ? (
                    <p className="no-exceptions-text">Não há exceções</p>
                  ) : (
                    <div className="exceptions-list">
                      {exceptions.map((exception) => (
                        <div
                          className="exception-item-wrapper"
                          key={exception.id}
                        >
                          <div className="exception-reason-row">
                            <div className="exception-input-group full-width">
                              <label>Escreva o motivo</label>
                              <input
                                type="text"
                                placeholder="Escreva o motivo da ausência"
                                value={exception.reason}
                                onChange={(e) =>
                                  updateException(
                                    exception.id,
                                    'reason',
                                    e.target.value
                                  )
                                }
                                className="reason-input"
                              />
                            </div>
                          </div>
                          <div className="exception-fields-row">
                            <div className="exception-input-group">
                              <label>Data de início</label>
                              <div className="exception-input-wrapper">
                                <CalendarToday style={{ fontSize: 14 }} />
                                <input
                                  type="text"
                                  placeholder="DD/MM/AAAA"
                                  value={exception.startDate}
                                  onChange={(e) =>
                                    updateException(
                                      exception.id,
                                      'startDate',
                                      e.target.value
                                    )
                                  }
                                  maxLength={10}
                                />
                              </div>
                            </div>
                            <div className="exception-input-group">
                              <label>Hora inicial</label>
                              <div className="exception-input-wrapper">
                                <AccessTime style={{ fontSize: 14 }} />
                                <input
                                  type="time"
                                  value={exception.startTime}
                                  onChange={(e) =>
                                    updateException(
                                      exception.id,
                                      'startTime',
                                      e.target.value
                                    )
                                  }
                                />
                              </div>
                            </div>
                            <div className="exception-input-group">
                              <label>Hora final</label>
                              <div className="exception-input-wrapper">
                                <AccessTime style={{ fontSize: 14 }} />
                                <input
                                  type="time"
                                  value={exception.endTime}
                                  onChange={(e) =>
                                    updateException(
                                      exception.id,
                                      'endTime',
                                      e.target.value
                                    )
                                  }
                                />
                              </div>
                            </div>
                            <button
                              type="button"
                              className="remove-exception-button"
                              onClick={() => removeException(exception.id)}
                            >
                              <Delete style={{ fontSize: 16 }} />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </S.ExceptionsSection>

                <S.PageFooter>
                  <button
                    type="button"
                    className="cancel-button"
                    onClick={() => {
                      if (savedSchedules.length > 0) {
                        setShowNewPeriodForm(false)
                      } else {
                        goBack()
                      }
                    }}
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    className="submit-button"
                    onClick={handleSubmit}
                  >
                    Cadastrar novo período
                  </button>
                </S.PageFooter>
              </>
            )}
          </S.PageContent>
        </Card>
      </S.PageContainer>
      <Footer />

      {deleteModal.isOpen && (
        <S.DeleteModalOverlay>
          <S.DeleteModal>
            <h3>Excluir período cadastrado</h3>
            <p>
              Apenas os horários livres serão excluídos, os horários já
              agendados não serão afetados. Você realmente deseja excluir o
              período de {deleteModal.startDate} a {deleteModal.endDate}?
            </p>
            <div className="modal-actions">
              <button
                type="button"
                className="cancel-button"
                onClick={closeDeleteModal}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="delete-button"
                onClick={confirmDeleteSchedule}
              >
                Excluir
              </button>
            </div>
          </S.DeleteModal>
        </S.DeleteModalOverlay>
      )}
    </Global.Container>
  )
}

export default OpenSchedule
